import { useQueryClient } from "@tanstack/react-query";

import { AuthContext } from "@/data/auth/auth.context";
import type { CommonModels } from "@/data/common/common.models";
import { GuestProfileQueries } from "@/data/guestProfile/guestProfile.queries";
import { ProfileQueries } from "@/data/profile/profile.queries";
import { ProjectsQueries } from "@/data/projects/projects.queries";
import { WorthDataQueries } from "@/data/worthData/worthData.queries";

const useInvalidateProfile = () => {
  const queryClient = useQueryClient();
  const { syncProfile } = AuthContext.useAuth();

  const invalidateProfile = () => {
    queryClient.invalidateQueries({ queryKey: [...ProfileQueries.keys.getMy()] });
  };

  const invalidateProfileProjects = async () => {
    await queryClient.invalidateQueries({ queryKey: ProjectsQueries.keys.getFeatured() });
    await queryClient.invalidateQueries({ queryKey: [...ProjectsQueries.keys.get(50)] });
  };

  const invalidateProfileWorthData = async () => {
    await queryClient.invalidateQueries({ queryKey: WorthDataQueries.keys.get() });
  };

  const invalidateAndRefetchProfile = async () => {
    invalidateProfile();
    await invalidateProfileProjects();
    await invalidateProfileWorthData();
    await syncProfile();
  };

  const invalidateGuestProfile = () => {
    queryClient.invalidateQueries({ queryKey: GuestProfileQueries.keys.getCurrent() });
  };

  const updateProfileOnboardingStep = async (onboardingStep: CommonModels.OnboardingStepEnum) => {
    queryClient.setQueryData(ProfileQueries.keys.getMy(), (oldData: any) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        onboardingStep,
      };
    });
  };

  const updateProfileStatusAndOnboardingStep = async (
    status: CommonModels.ProfileStatusEnum,
    onboardingStep: CommonModels.OnboardingStepEnum,
  ) => {
    queryClient.setQueryData(ProfileQueries.keys.getMy(), (oldData: any) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        status,
        onboardingStep,
      };
    });
    await syncProfile();
  };

  return {
    invalidateProfile,
    invalidateAndRefetchProfile,
    invalidateProfileProjects,
    invalidateProfileWorthData,
    invalidateGuestProfile,
    updateProfileOnboardingStep,
    updateProfileStatusAndOnboardingStep,
  };
};

export default useInvalidateProfile;
